import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactSelect from 'react-select';
import axios from 'axios';
import { useAuth } from '../AuthContext';
const { rootUrl } = require('../../../config/config');

const InspectionCreate = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const token = localStorage.getItem('authToken') || 'default-token';
    const authHeaders = { Authorization: `Bearer ${token}` };

    const [cars, setCars] = useState([]);
    const [selectedPlate, setSelectedPlate] = useState(null);
    const [carMileage, setCarMileage] = useState(null);
    const [carMileageLoading, setCarMileageLoading] = useState(false);
    const [openInspectionWarning, setOpenInspectionWarning] = useState('');
    const [form, setForm] = useState({
        inspection_date: new Date().toISOString().split('T')[0],
        inspector_name: '',
        mileage: '',
        notes: '',
    });
    const [mileageError, setMileageError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios.get(`${rootUrl}/cars`, { headers: authHeaders })
            .then(res => setCars(res.data || []))
            .catch(err => console.error('Failed to load cars:', err));
    }, []);

    const handlePlateSelect = async (option) => {
        setSelectedPlate(option);
        setCarMileage(null);
        setOpenInspectionWarning('');
        setMileageError('');
        setSubmitError('');
        if (!option) return;

        setCarMileageLoading(true);
        try {
            const [carRes, inspRes] = await Promise.all([
                axios.get(`${rootUrl}/cars/${encodeURIComponent(option.value)}`, { headers: authHeaders }),
                axios.get(`${rootUrl}/cars/inspections/internal/plate/${encodeURIComponent(option.value)}`, { headers: authHeaders }),
            ]);
            const car = carRes.data;
            setCarMileage({ current_mileage: car.current_mileage, last_mileage_date: car.last_mileage_date });

            const openInspections = (inspRes.data || []).filter(
                i => i.inspection_status === 'in_progress' || i.inspection_status === 'scheduled'
            );
            if (openInspections.length > 0) {
                setOpenInspectionWarning('This vehicle already has an open inspection. Complete or cancel it before creating a new one.');
            }

            // Re-validate mileage with new car data
            if (form.mileage && car.current_mileage != null && parseInt(form.mileage) < car.current_mileage) {
                setMileageError(`Cannot go backwards — last recorded was ${car.current_mileage.toLocaleString()} km`);
            } else {
                setMileageError('');
            }
        } catch (err) {
            console.error('Failed to fetch car data:', err);
            setSubmitError('Failed to load vehicle data. Please try again or select another plate.');
        } finally {
            setCarMileageLoading(false);
        }
    };

    const handleMileageChange = (val) => {
        setForm(prev => ({ ...prev, mileage: val }));
        if (carMileage?.current_mileage != null && val && parseInt(val) < carMileage.current_mileage) {
            setMileageError(`Cannot go backwards — last recorded was ${carMileage.current_mileage.toLocaleString()} km`);
        } else {
            setMileageError('');
        }
    };

    const formatMileageHint = () => {
        if (!carMileage) return null;
        if (carMileage.current_mileage == null) return 'No mileage on record for this vehicle';
        const date = carMileage.last_mileage_date
            ? new Date(carMileage.last_mileage_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : 'unknown date';
        return `Last recorded: ${carMileage.current_mileage.toLocaleString()} km on ${date}`;
    };

    const isSubmitDisabled =
        !selectedPlate ||
        !form.inspection_date ||
        !form.inspector_name ||
        !form.mileage ||
        !form.notes ||
        !!mileageError ||
        !!openInspectionWarning ||
        carMileageLoading ||
        submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        setSubmitting(true);
        try {
            const res = await axios.post(
                `${rootUrl}/cars/inspections/internal`,
                {
                    plate_no: selectedPlate.value,
                    inspection_date: form.inspection_date,
                    inspector_name: form.inspector_name,
                    mileage: parseInt(form.mileage),
                    notes: form.notes,
                    input_by: user?.username || 'system',
                },
                { headers: authHeaders }
            );
            navigate(`/inspections/internal/${res.data.data.id}/conduct`);
        } catch (err) {
            setSubmitError(err.response?.data?.message || err.response?.data?.error || 'Failed to create inspection.');
        } finally {
            setSubmitting(false);
        }
    };

    const plateOptions = cars.map(car => ({ label: car.plate_no, value: car.plate_no }));

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-xl mx-auto px-4 py-6">
                <div className="flex items-center gap-3 mb-6">
                    <button
                        type="button"
                        onClick={() => navigate('/inspections/internal')}
                        className="text-gray-600 font-medium flex items-center gap-1"
                    >
                        ← Back
                    </button>
                    <h1 className="text-xl font-semibold text-gray-800">New Internal Inspection</h1>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 space-y-5">
                    {submitError && (
                        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                            {submitError}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plate Number *</label>
                        <ReactSelect
                            options={plateOptions}
                            value={selectedPlate}
                            onChange={handlePlateSelect}
                            placeholder="Search plate number..."
                            isClearable
                            classNamePrefix="react-select"
                        />
                    </div>

                    {openInspectionWarning && (
                        <div className="p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded text-sm">
                            {openInspectionWarning}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Date *</label>
                        <input
                            type="date"
                            value={form.inspection_date}
                            onChange={e => setForm(prev => ({ ...prev, inspection_date: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Inspector Name *</label>
                        <input
                            type="text"
                            value={form.inspector_name}
                            onChange={e => setForm(prev => ({ ...prev, inspector_name: e.target.value.toUpperCase() }))}
                            placeholder="INSPECTOR NAME"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mileage (km) *</label>
                        <input
                            type="number"
                            value={form.mileage}
                            onChange={e => handleMileageChange(e.target.value)}
                            placeholder="0"
                            min="0"
                            className={`w-full border rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 ${mileageError ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-brand-primary'}`}
                            required
                        />
                        {carMileageLoading && (
                            <p className="text-xs text-gray-400 mt-1">Loading mileage data...</p>
                        )}
                        {!carMileageLoading && selectedPlate && carMileage !== null && (
                            <p className="text-xs text-gray-500 mt-1">{formatMileageHint()}</p>
                        )}
                        {mileageError && (
                            <p className="text-xs text-red-500 mt-1">{mileageError}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes *</label>
                        <textarea
                            value={form.notes}
                            onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Pre-inspection notes..."
                            rows={3}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
                            required
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={() => navigate('/inspections/internal')}
                            className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg text-sm font-medium hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitDisabled}
                            className="flex-1 bg-brand-primary text-white py-3 rounded-lg text-sm font-medium disabled:opacity-50"
                        >
                            {submitting ? 'Creating...' : 'Create & Start'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InspectionCreate;
